/**
 * QuarterlyUpdateCard Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays quarterly update data in an interactive card grid layout with PDF viewing capabilities.
 * Each card represents a quarterly update with visual indicators for current quarter, sorting by
 * recency, and search highlighting functionality.
 * 
 * Features:
 * - Card-based display of quarterly updates
 * - Current quarter highlighting with special badge
 * - Quarter-based color coding (Q1-Q4)
 * - PDF modal integration for document viewing
 * - Search query highlighting
 * - Automatic sorting by year and quarter (most recent first)
 * - No-results state handling
 * - Tooltips for additional information
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of quarterly update objects to display
 * @param {string} [props.searchQuery=''] - Search query string for highlighting matches
 * @returns {JSX.Element} The rendered quarterly update cards component
 * 
 * @example
 * ```jsx
 * <QuarterlyUpdateCard 
 *   data={quarterlyData} 
 *   searchQuery="Q1" 
 * />
 * ```
 */

import React, { useState, useMemo } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import PDFModal from '../../modals/PDFModal';
import { formatDate, normalizeText } from '../../utils/textUtils';
import './QuarterlyUpdateCard.css';


function QuarterlyUpdateCard({ data, searchQuery = '' }) {
    const [modals, setModals] = useState([]);

    /**
     * Get CSS color class for quarter
     * 
     * Maps quarter values (Q1-Q4) to predefined color classes for consistent
     * visual differentiation across quarters.
     * 
     * @param {string} quarter - The quarter string (Q1, Q2, Q3, Q4)
     * @returns {string} CSS class name for the quarter color
     * 
     * @example
     * ```javascript
     * getQuarterColor('Q1') // Returns 'q1'
     * getQuarterColor('Q3') // Returns 'q3'
     * getQuarterColor('unknown') // Returns 'q1' (default)
     * ```
     */
    const getQuarterColor = (quarter) => {
        const colorMap = {
            'Q1': 'q1',
            'Q2': 'q2',
            'Q3': 'q3',
            'Q4': 'q4'
        };
        return colorMap[quarter] || 'q1';
    };

    /**
     * Check if the given year and quarter represents the current quarter
     * 
     * Compares the provided year and quarter against the current date to determine
     * if this represents the current business quarter.
     * 
     * @param {string|number} year - The year to check
     * @param {string} quarter - The quarter to check (Q1, Q2, Q3, Q4)
     * @returns {boolean} True if this is the current quarter
     * 
     * @example
     * ```javascript
     * isCurrentQuarter('2024', 'Q1') // Returns true if current date is in Q1 2024
     * isCurrentQuarter('2023', 'Q4') // Returns false if not current quarter
     * ```
     */
    const isCurrentQuarter = (year, quarter) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth(); // 0-based
        const currentQuarter = Math.floor(currentMonth / 3) + 1;
        return parseInt(year) === currentYear && quarter === `Q${currentQuarter}`;
    };

    /**
     * Convert quarter string to number for sorting
     * 
     * Utility function to convert quarter strings (Q1-Q4) to numeric values
     * for proper sorting operations.
     * 
     * @param {string} quarter - Quarter string (Q1, Q2, Q3, Q4)
     * @returns {number} Numeric value for sorting (1-4)
     * 
     * @example
     * ```javascript
     * getQuarterNumber('Q1') // Returns 1
     * getQuarterNumber('Q4') // Returns 4
     * getQuarterNumber('unknown') // Returns 0
     * ```
     */
    const getQuarterNumber = (quarter) => {
        const quarterMap = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
        return quarterMap[quarter] || 0;
    };

    /**
     * Sort data by Year and Quarter in descending order
     * 
     * Sorts the quarterly update data to show the most recent quarters first.
     * Primary sort by year (descending), secondary sort by quarter (descending).
     * 
     * @returns {Array} Sorted array of quarterly update objects
     */
    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const yearDiff = parseInt(b.Year, 10) - parseInt(a.Year, 10);
            return yearDiff !== 0 ? yearDiff : getQuarterNumber(b.Quarter) - getQuarterNumber(a.Quarter);
        });
    }, [data]);

    /**
     * Open a PDF modal or bring existing modal to front
     * 
     * Manages multiple PDF modals with proper z-index stacking. If a modal for the same
     * quarterly update already exists, it brings it to the front. Otherwise, creates a new modal
     * with proper z-index management.
     * 
     * @param {string|number} id - Unique identifier for the quarterly update
     * @param {string} title - Title to display in the modal header
     * @param {string} pdfUrl - URL of the PDF document to display
     * 
     * @example
     * ```javascript
     * openPDFModal('q1-2024', 'Q1 March 2024', 'https://example.com/q1-2024.pdf')
     * ```
     */
    const openPDFModal = (id, title, pdfUrl) => {
        setModals((prevModals) => {
            const existingModalIndex = prevModals.findIndex((modal) => modal.id === id);
            if (existingModalIndex !== -1) {
                // If modal is already open, bring it to the front by updating zIndex
                const updatedModals = [...prevModals];
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex)) + 1;
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                // Open a new modal with calculated positioning and size
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex), 0) + 1;
                const newModal = {
                    id,
                    title,
                    pdfUrl,
                    isOpen: true,
                    zIndex: maxZIndex
                };
                return [...prevModals, newModal];
            }
        });
    };

    /**
     * Close a PDF modal by ID
     * 
     * Marks the specified modal as closed while maintaining it in the modals array
     * for potential reopening. The modal component handles the actual visibility.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * closePDFModal('q1-2024')
     * ```
     */
    const closePDFModal = (id) => {
        setModals((prevModals) =>
            prevModals.map((modal) =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
    };

    if (!data || data.length === 0) {
        return (
            <section className="quarterly-update-cards-section">
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No quarterly updates found</h3>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="quarterly-update-cards-section">
            <div className="quarterly-update-cards-container">
                <div className="quarterly-update-cards-grid">
                    {sortedData.map((item) => (
                        <div
                            key={item.id}
                            className="quarterly-update-card"
                            onClick={() =>
                                openPDFModal(
                                    item.id,
                                    `${item.Quarter} ${item.Month} ${item.Year}`,
                                    item.doc
                                )
                            }
                        >
                            {/* Quarter Badge */}
                            {isCurrentQuarter(item.Year, item.Quarter) && (
                                <div className="quarter-badge current">
                                    <span>CURRENT</span>
                                </div>
                            )}

                            {/* Date Header */}
                            <div className="quarterly-update-card-date-header">
                                <div className="quarter-icon">
                                    <i className="fas fa-calendar-check"></i>
                                </div>
                                <div className="year-display-top">
                                    <i className="fas fa-calendar-alt"></i>
                                    <span>{item.Year}</span>
                                </div>
                            </div>

                            {/* Card Content */}
                            <div className="quarterly-update-card-content">
                                <Tippy content={`${item.Quarter} ${item.Month} ${item.Year}`}>
                                    <h3 className="quarterly-update-card-title">
                                        <span className="quarter-title-text">
                                            {searchQuery.trim() ? (
                                                <span dangerouslySetInnerHTML={{ 
                                                    __html: normalizeText(`${item.Quarter} ${item.Month} ${item.Year}`).replace(
                                                        new RegExp(searchQuery.trim(), 'gi'), 
                                                        match => `<span class="highlight-match">${match}</span>`
                                                    )
                                                }} />
                                            ) : (
                                                `${item.Quarter} ${item.Month} ${item.Year}`
                                            )}
                                        </span>
                                    </h3>
                                </Tippy>

                                {item.PublishedDate && (
                                    <div className="quarterly-update-card-summary">
                                        <Tippy content={`Published: ${formatDate(item.PublishedDate)}`}>
                                            <p>
                                                <strong>Published:</strong> {formatDate(item.PublishedDate)}
                                            </p>
                                        </Tippy>
                                    </div>
                                )}
                            </div>

                            {/* Card Footer - Tags */}
                            <div className="quarterly-update-card-footer">
                                <div className="quarterly-update-card-tags">
                                    <Tippy content={`Quarter: ${item.Quarter}`}>
                                        <span className={`quarterly-update-tag quarterly-update-tag-${getQuarterColor(item.Quarter)}`}>
                                            {item.Quarter}
                                        </span>
                                    </Tippy>
                                    
                                    {item.Year && (
                                        <Tippy content={`Year: ${item.Year}`}>
                                            <span className="quarterly-update-tag quarterly-update-tag-year">
                                                {item.Year}
                                            </span>
                                        </Tippy>
                                    )}
                                    
                                    {item.Month && (
                                        <Tippy content={`Month: ${item.Month}`}>
                                            <span className="quarterly-update-tag quarterly-update-tag-month">
                                                {item.Month}
                                            </span>
                                        </Tippy>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Render multiple PDF Modals */}
            {modals.map((modal) => (
                modal.isOpen && (
                    <PDFModal
                        key={modal.id}
                        title={modal.title}
                        url={modal.pdfUrl}
                        show={modal.isOpen}
                        onClose={(e) => {
                            e.stopPropagation();
                            closePDFModal(modal.id);
                        }}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                        onBringToFront={() => openPDFModal(modal.id, modal.title, modal.pdfUrl)}
                        listItemId={modal.id}
                    />
                )
            ))}
        </section>
    );
}

export default QuarterlyUpdateCard;
